import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { GlobalProps } from '@bloomberg/media-ui-core';
import { classes, useSlug } from '@bloomberg/media-ui-core';
import type { Placement } from '@floating-ui/core';
import {
  autoPlacement,
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { useQuickAdd } from '@foundation/quickadd-provider';

import { DEFAULT_WATCHLIST_NAME } from '../QuickAddOverlay/AddToWatchlist';
import type { WatchlistSaveForm } from '../types';
import { QuickAddIcon, QuickAddOverlay, QuickAddRoundedToggle } from '..';

import styles from './quickAddContainer.module.css';

const THREE_SECONDS = 3000;

const ICON_VARIANTS = new Set([
  'icon',
  'icon-with-text',
  'icon-with-small-text',
]);

const ICON_WITH_TEXT_VARIANTS = new Set([
  'icon-with-text',
  'icon-with-small-text',
]);

export type QuickAddContainerProps = GlobalProps & {
  ticker: string;
  className?: string;
  name?: string;
  variant?: 'button' | 'icon' | 'icon-with-text' | 'icon-with-small-text';
  trackClick?: (eventAction: string, eventLabel: string) => void;
  disabled?: boolean;
  allowedPlacements?: Array<Placement>;
  overlayOffset?: number;
  dismissOnOutsideClick?: boolean;
};

export function QuickAddContainer({
  ticker,
  className,
  trackClick,
  variant = 'button',
  name = 'quick-add-container',
  disabled = true,
  allowedPlacements = ['bottom-start', 'bottom-end'],
  overlayOffset = 5,
  dismissOnOutsideClick = true,
}: QuickAddContainerProps) {
  const [componentName] = useSlug(name);
  const {
    controller: qac,
    watchlistSummarysForTickers,
    isLoggedIn,
    openSignIn,
    openSignUp,
    isAtMaximumWatchlist,
    openOverlay,
    isUpdating,
    setOpenOverlay,
    subscriberType,
  } = useQuickAdd();

  const [floatingRef, setFloatingRef] = useState<HTMLElement | null>(null);
  const [anchorRef, setAnchorRef] = useState<Element | null>(null);

  const isLoaded = useMemo(
    () => Boolean(watchlistSummarysForTickers?.[ticker]) && !isUpdating,
    [isUpdating, watchlistSummarysForTickers, ticker]
  );
  const watchlists = useMemo(
    () => watchlistSummarysForTickers?.[ticker] || [],
    [watchlistSummarysForTickers, ticker]
  );
  const isFollowing = useMemo(
    () => Boolean(watchlists?.some(({ isFollowing: isF }) => isF)),
    [watchlists]
  );

  const [shouldShowError, setShouldShowError] = useState<boolean>(false);
  const showError = useCallback(() => {
    setShouldShowError(true);
  }, []);
  const clearError = useCallback(() => {
    setShouldShowError(false);
  }, []);
  const componentId = useMemo<string>(
    () => Math.random().toString(20).slice(2),
    []
  );
  const isOpen = useMemo<boolean>(
    () => componentId === openOverlay,
    [componentId, openOverlay]
  );
  const open = useCallback(() => {
    setOpenOverlay?.(componentId);
  }, [componentId, setOpenOverlay]);
  const close = useCallback(() => {
    setOpenOverlay?.(null);
  }, [setOpenOverlay]);

  const toastError = useCallback(() => {
    showError();
    open();

    setTimeout(() => {
      close();
    }, THREE_SECONDS);
  }, [open, close, showError]);

  const signIn = useCallback(() => {
    if (disabled) {
      trackClick?.('click', 'sign-in-add-remove');
    } else {
      trackClick?.('click', 'sign-in-quick-add');
    }
    if (openSignIn) {
      openSignIn();
      close();
    }
  }, [openSignIn, close, trackClick]);

  const signUp = useCallback(() => {
    trackClick?.('click', 'create-account-quick-add');

    if (openSignUp) {
      openSignUp();
      close();
    }
  }, [openSignUp, close]);

  const saveToWatchlists = useCallback(
    (saveForm: WatchlistSaveForm) => {
      clearError();
      trackClick?.('click', `save-changes-quick-add`);

      if (!qac) {
        toastError();

        return;
      }

      const requests: Promise<any>[] = []; // eslint-disable-line @typescript-eslint/no-explicit-any

      if (saveForm.createDefaultWatchlist) {
        requests.push(
          qac.createWatchlist({
            ticker,
            name: DEFAULT_WATCHLIST_NAME,
            isDefault: true,
          })
        );
      }

      if (saveForm.adds.length) {
        saveForm.adds.forEach(() =>
          trackClick?.('click', `quick-add-add-${ticker}`)
        );

        requests.push(
          qac.addTickerToWatchlists({
            ticker,
            watchlistIds: saveForm.adds,
          })
        );
      }

      if (saveForm.deletes.length) {
        saveForm.deletes.forEach(() =>
          trackClick?.('click', `quick-add-remove-${ticker}`)
        );

        requests.push(
          qac.removeTickerFromWatchlists({
            ticker,
            watchlistIds: saveForm.deletes,
          })
        );
      }

      Promise.all(requests)
        .catch(() => toastError())
        .then(() =>
          qac
            .updateTickerWatchlists()
            .catch(() => toastError())
            .then(() => close())
        );
    },
    [qac, ticker, close, clearError, toastError, trackClick]
  );

  const onOverlayClose = useCallback(() => {
    trackClick?.('click', 'close-overlay-quick-add');
    close();
  }, [trackClick, close]);

  const createNewWatchlist = useCallback(
    (watchlistName: string) => {
      clearError();
      trackClick?.('click', 'create-account-quick-add');

      if (!qac) {
        toastError();

        return Promise.reject(null);
      }

      return qac
        .createWatchlist({ name: watchlistName, isDefault: false })
        .then(({ data }) => {
          if (typeof data !== 'string' && data?.port?.portInfo?.id) {
            return data.port.portInfo.id;
          }

          toastError();

          return null;
        })
        .catch(() => {
          toastError();

          return null;
        });
    },
    [qac, clearError, toastError, trackClick]
  );

  const isSignedIn = useMemo(() => Boolean(isLoggedIn), [isLoggedIn]);

  useEffect(() => {
    // having a qac should mean they are signed in
    if (qac) {
      qac.registerTicker(ticker);
      qac.updateTickerWatchlists();
    }
  }, [qac, ticker]);

  const isOverlayOpen = useMemo(
    () => isOpen && (!isSignedIn || isLoaded || shouldShowError),
    [isOpen, isSignedIn, isLoaded]
  );

  const { update, refs, context, floatingStyles } = useFloating({
    placement: 'bottom-start',
    transform: false,
    middleware: [
      offset(overlayOffset),
      autoPlacement({
        allowedPlacements,
        padding: 5,
      }),
      flip({
        rootBoundary: 'viewport',
        fallbackPlacements: ['bottom-end'],
        fallbackStrategy: 'bestFit',
      }),
      shift({}),
    ],
    whileElementsMounted: autoUpdate,
    strategy: 'absolute',
    open: isOverlayOpen,
    onOpenChange: (next) => (next ? open() : close()),
  });

  const follow = useCallback(() => {
    if (isOpen) {
      return;
    }

    trackClick?.('click', 'quick-add-follow');

    if (!isLoggedIn) {
      open();

      return;
    }

    if (!qac) {
      toastError();

      return;
    }

    if (!isLoaded) {
      qac.registerTicker(ticker);
    }

    qac
      .updateTickerWatchlists()
      .catch(() => toastError())
      .then(() => {
        open();
      });

    requestAnimationFrame(() => update());
  }, [open, isOpen, qac, ticker, isLoaded, isLoggedIn, toastError]);

  const role = useRole(context);
  const dismiss = useDismiss(context, {
    outsidePress: dismissOnOutsideClick,
    escapeKey: dismissOnOutsideClick,
    outsidePressEvent: 'pointerdown',
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    dismiss,
    role,
  ]);

  useEffect(() => {
    if (refs.reference.current !== anchorRef) {
      refs.setReference(anchorRef);
      update();
    }
  }, [anchorRef]);

  useEffect(() => {
    if (floatingRef && refs.floating.current !== floatingRef) {
      refs.setFloating(floatingRef);
      update();
    }
  }, [floatingRef]);

  const isIconVariant = useMemo(() => ICON_VARIANTS.has(variant), [variant]);

  return (
    <div
      className={classes([
        styles.quickAddContainer,
        variant === 'button' && styles.withButton,
        isIconVariant && styles.withIcon,
        className,
      ])}
      data-component={componentName}
    >
      {variant === 'button' && (
        <QuickAddRoundedToggle
          isFollowing={isFollowing}
          onClick={follow}
          ref={setAnchorRef}
        />
      )}
      {isIconVariant && (
        <QuickAddIcon
          size={variant === 'icon-with-small-text' ? 'small' : 'default'}
          showText={ICON_WITH_TEXT_VARIANTS.has(variant)}
          isFollowing={isFollowing}
          onClick={follow}
          ref={setAnchorRef}
          {...getReferenceProps()}
        />
      )}
      {isOverlayOpen && (
        <FloatingPortal id={'quick-add'}>
          <FloatingFocusManager
            initialFocus={-1}
            returnFocus
            modal={false}
            context={context}
          >
            <div
              className={styles.floatingUI}
              ref={setFloatingRef}
              style={{ ...floatingStyles }}
              {...getFloatingProps()}
            >
              <QuickAddOverlay
                trackClick={trackClick}
                className={styles.quickAddOverlay}
                watchlists={watchlists}
                onCreateAccountClick={signUp}
                onSignInClick={signIn}
                canAddWatchlist={!isAtMaximumWatchlist}
                onClose={onOverlayClose}
                showError={shouldShowError}
                onSave={saveToWatchlists}
                onCreateNewWatchlist={createNewWatchlist}
                isLoggedIn={isSignedIn}
                isFollowing={isFollowing}
                isSubscribed={subscriberType === 'BBG'}
                disabled={disabled}
              />
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </div>
  );
}

QuickAddContainer.ignoreCommittee = true;
